/**
 * Global behaviour loaded on every page.
 *
 * Drawers and modals are native <dialog> elements: showModal() already gives
 * focus trapping, Esc to close and an inert page behind, so no focus-trap code.
 *
 *   <button data-dialog-open="cart-drawer">Cart</button>
 *   <dialog id="cart-drawer" class="drawer"> ... <button data-dialog-close>×</button></dialog>
 */
// --header-bottom: where the visible header ends (drill-down menu, menu overlay, dropdown height).
const rzSetHeaderBottom = () => {
  const header = document.querySelector('.header');
  if (header) document.documentElement.style.setProperty('--header-bottom', `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`);
};
// A dropdown that would run past the right edge of the window opens towards the left, under its menu item.
// ponytail: left-to-right only; mirror the check if a right-to-left store ever needs it.
const rzAlignDropdown = (item) => {
  const dropdown = item?.querySelector(':scope > .dropdown');
  if (!dropdown) return;
  dropdown.classList.remove('dropdown--end');
  dropdown.classList.toggle('dropdown--end', dropdown.getBoundingClientRect().right > document.documentElement.clientWidth);
};
// Sidebar mega menu (as Ultra's): a row becomes active when the pointer rests on it and stays active while the pointer
// moves on to its links (across the gap or over other rows on the way), so the panel never jumps to another row.
// Keyboard focus switches at once.
{
  let timer;
  const activate = (row) => {
    clearTimeout(timer);
    row.parentElement.querySelectorAll(':scope > .mega__column.is-active').forEach((r) => r !== row && r.classList.remove('is-active'));
    row.classList.add('is-active');
  };
  document.addEventListener('pointerover', (event) => {
    const row = event.target.closest?.('.mega--sidebar .mega__columns > .mega__column');
    if (!row || row.classList.contains('is-active') || event.target.closest('.mega__list')) return;
    clearTimeout(timer);
    timer = setTimeout(() => activate(row), 120);
  });
  document.addEventListener('pointerout', (event) => {
    const row = event.target.closest?.('.mega--sidebar .mega__columns > .mega__column');
    if (row && !row.contains(event.relatedTarget)) clearTimeout(timer);
  });
  document.addEventListener('focusin', (event) => {
    const row = event.target.closest?.('.mega--sidebar .mega__columns > .mega__column');
    if (row) activate(row);
  });
}
// Closed dropdowns are only hidden, so one past the right edge still widens the page (a horizontal scrollbar):
// align them all once the submenus are in the page and whenever the window changes size.
const rzAlignDropdowns = () => document.querySelectorAll('.header__nav .header__item--parent').forEach(rzAlignDropdown);
{
  let frame;
  addEventListener('resize', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(rzAlignDropdowns);
  });
}
document.addEventListener('pointerover', (event) => {
  if (!event.target.closest?.('.header__nav')) return;
  rzSetHeaderBottom();
  rzAlignDropdown(event.target.closest('.header__item--parent'));
});
document.addEventListener('focusin', (event) => {
  rzAlignDropdown(event.target.closest?.('.header__nav .header__item--parent'));
});
// Esc closes the desktop dropdown or mega panel under the pointer or focus and returns focus to its menu item;
// it opens again once the pointer or focus has left that item.
const rzReopenMenuItem = (event) => {
  const item = event.target.closest?.('.header__item.is-closed');
  if (item && !item.contains(event.relatedTarget)) item.classList.remove('is-closed');
};
document.addEventListener('focusout', rzReopenMenuItem);
document.addEventListener('pointerout', rzReopenMenuItem);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.dialogOpen);
    if (dialog instanceof HTMLDialogElement) {
      event.preventDefault();
      dialog.querySelectorAll('rz-header-menu').forEach((menu) => menu.hydrateSubmenus?.());
      // data-nonmodal (drill-down menu): opens under the header, which stays usable; the same button closes it.
      if (dialog.hasAttribute('data-nonmodal')) {
        if (dialog.open) {
          dialog.close();
        } else {
          rzSetHeaderBottom();
          dialog.show();
        }
        opener.setAttribute('aria-expanded', String(dialog.open));
      } else {
        dialog.showModal();
      }
    }
    return;
  }

  // Back button of a drill-down panel: closes its <details> and any accordion open inside it.
  const back = event.target.closest('[data-drawer-back]');
  if (back) {
    const panel = back.closest('details');
    panel?.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
    panel?.removeAttribute('open');
    return;
  }

  const closer = event.target.closest('[data-dialog-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }

  // Click on the backdrop: the event target is the <dialog> itself, outside its content box.
  if (event.target instanceof HTMLDialogElement && event.target.open) {
    const rect = event.target.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!inside) event.target.close();
  }
});

// A mouse click on a dropdown accordion would leave focus there, and :focus-within would keep that dropdown open
// after the pointer moves to another menu item. Keyboard toggles (detail 0) keep their focus.
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('.dropdown__toggle');
  if (toggle && event.detail > 0) toggle.blur();
});

// Esc closes a non-modal dialog too (modal ones close natively).
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const item = document.activeElement?.closest('.header__nav .header__item--parent') || document.querySelector('.header__nav .header__item--parent:hover');
  if (item) {
    item.classList.add('is-closed');
    item.querySelector('.header__link')?.focus();
  }
  document.querySelectorAll('dialog[data-nonmodal][open]').forEach((dialog) => {
    dialog.close();
    document.querySelector(`[data-dialog-open="${dialog.id}"]`)?.setAttribute('aria-expanded', 'false');
  });
});

// <form data-autosubmit>: submit as soon as a select changes (e.g. country/language).
document.addEventListener('change', (event) => {
  const form = event.target.closest('form[data-autosubmit]');
  if (form && event.target.matches('select')) form.requestSubmit();
});

// <details data-open-desktop>: open on desktop, collapsed on mobile (footer menus).
{
  const desktop = matchMedia('(min-width: 750px)');
  const sync = () =>
    document.querySelectorAll('details[data-open-desktop]').forEach((d) => (d.open = desktop.matches));
  desktop.addEventListener('change', sync);
  document.addEventListener('DOMContentLoaded', sync);
  document.addEventListener('shopify:section:load', sync);
}

// Product card swatches: hovering or focusing a color shows that variant's image on the card.
{
  const preview = (event) => {
    const swatch = event.target.closest?.('.card-swatch[data-image]');
    if (!swatch) return;
    const card = swatch.closest('.product-card');
    const img = card?.querySelector('.product-card__image:not(.product-card__image--second) img');
    if (!img) return;
    img.srcset = swatch.dataset.srcset;
    img.src = swatch.dataset.image;
    card.querySelectorAll('.card-swatch.is-active').forEach((s) => s.classList.remove('is-active'));
    swatch.classList.add('is-active');
  };
  document.addEventListener('mouseover', preview);
  document.addEventListener('focusin', preview);
}

// --header-height: height of the sticky header group, so sticky sidebars sit below it.
{
  // Sticky comes from the header setting (data-sticky), not getComputedStyle: no style recalculation at load.
  const header = document.querySelector('.section-header');
  if (header?.querySelector('header[data-sticky]')) {
    new ResizeObserver(([entry]) =>
      document.documentElement.style.setProperty('--header-height', `${Math.round(entry.borderBoxSize[0].blockSize)}px`)
    ).observe(header);
  }
}

// <rz-header-menu>: header submenus (desktop dropdowns/mega panels, mobile drawer sub-lists) arrive as inert
// <template data-main-nav-child>, so the page starts with a much smaller DOM. They go live on the first pointer,
// touch or focus inside the menu (before the hover/click that opens them), or at once in the theme editor.
// The desktop menu also goes live when the browser is idle after load (crawlers see the links); the drawer's copy
// only when the drawer opens.
if (!customElements.get('rz-header-menu')) {
  customElements.define(
    'rz-header-menu',
    class extends HTMLElement {
      connectedCallback() {
        if (!this.querySelector('template[data-main-nav-child]')) return;
        this.hydrateSubmenus = this.hydrateSubmenus.bind(this);
        // Theme editor, or the pointer/focus is already inside (moved in before this script ran).
        if (window.Shopify?.designMode || this.matches(':hover, :focus-within')) {
          this.hydrateSubmenus();
          return;
        }
        ['pointerenter', 'touchstart', 'focusin'].forEach((type) =>
          this.addEventListener(type, this.hydrateSubmenus, { once: true, passive: true })
        );
        // The mobile drawer's copy waits until the drawer opens (the opener calls hydrateSubmenus, and focus moves in):
        // most visitors never open it, and crawlers get the same links from the desktop menu below.
        if (this.closest('dialog')) return;
        const whenIdle = () =>
          'requestIdleCallback' in window
            ? requestIdleCallback(this.hydrateSubmenus, { timeout: 5000 })
            : setTimeout(this.hydrateSubmenus, 4000);
        document.readyState === 'complete' ? whenIdle() : window.addEventListener('load', whenIdle, { once: true });
      }

      // Runs once: later calls find no templates. Nothing inside needs re-binding: dropdowns and mega panels open
      // with CSS (:hover / :focus-within), drawer items are native <details>, <rz-slideshow> starts itself on connect.
      hydrateSubmenus() {
        this.querySelectorAll('template[data-main-nav-child]').forEach((tmpl) => tmpl.replaceWith(tmpl.content));
        rzAlignDropdowns();
      }
    }
  );
}

// Autoplay videos: Liquid writes data-autoplay instead of autoplay (an autoplay video is downloaded in full while the
// page loads, even far below the fold). Play when the video comes near the viewport, pause when it leaves.
{
  const observer = new IntersectionObserver(
    (entries) =>
      entries.forEach(({ target, isIntersecting }) => (isIntersecting ? target.play().catch(() => {}) : target.pause())),
    { rootMargin: '200px 0px' }
  );
  const watch = (root) =>
    root.querySelectorAll('video[data-autoplay]').forEach((video) => {
      video.muted = true; // browsers only autoplay muted video
      observer.observe(video);
    });
  watch(document);
  document.addEventListener('shopify:section:load', (event) => watch(event.target));
}

// <rz-deferred-media>: YouTube / Vimeo / 3D models load only when the visitor presses play.
if (!customElements.get('rz-deferred-media')) {
  customElements.define(
    'rz-deferred-media',
    class extends HTMLElement {
      connectedCallback() {
        this.querySelector('button')?.addEventListener('click', () => this.load(), { once: true });
      }

      load() {
        const template = this.querySelector('template');
        if (!template) return;
        const content = template.content.cloneNode(true);
        this.querySelector('button')?.remove();
        this.append(content);
        if (this.querySelector('model-viewer')) {
          window.Shopify?.loadFeatures?.([{ name: 'model-viewer-ui', version: '1.0' }]);
        }
      }
    }
  );
}

// <rz-copy data-copy="CODE">: copy a discount code with visible feedback.
if (!customElements.get('rz-copy')) {
  customElements.define(
    'rz-copy',
    class extends HTMLElement {
      connectedCallback() {
        const button = this.querySelector('button');
        button?.addEventListener('click', async () => {
          const label = this.querySelector('[data-copy-label]');
          if (this.hasAttribute('data-apply')) {
            const message = await this.apply();
            if (!message) return;
            if (label) label.textContent = message;
            const status = this.querySelector('[data-copy-status]');
            if (status) status.textContent = message;
            return;
          }
          await navigator.clipboard.writeText(this.dataset.copy);
          if (label) label.textContent = button.dataset.copied;
        });
      }

      // Adds the code to the cart's codes. Shopify says whether it applies now; a code for products
      // not in the cart yet stays on the cart and applies once they are added.
      async apply() {
        const root = window.Shopify?.routes?.root || '/';
        const code = this.dataset.copy;
        try {
          const current = await fetch(`${root}cart.js`).then((r) => r.json());
          const codes = (current.discount_codes || []).map((c) => c.code).filter((c) => c.toLowerCase() !== code.toLowerCase());
          const response = await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: [...codes, code].join(',') }),
          });
          if (!response.ok) return window.theme?.strings?.cartError;
          const cart = await response.json();
          await window.rzCart?.refresh();
          const entry = (cart.discount_codes || []).find((c) => c.code.toLowerCase() === code.toLowerCase());
          return entry?.applicable ? this.dataset.applied : this.dataset.appliedLater;
        } catch (e) {
          return window.theme?.strings?.cartError;
        }
      }
    }
  );
}

// Gift card "send to recipient": fields only submit (and email is only required) while open.
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!details.matches?.('details.gift-recipient')) return;
    details.querySelectorAll('[data-recipient-field], [data-recipient-flag]').forEach((f) => (f.disabled = !details.open));
    const email = details.querySelector('input[type="email"]');
    if (email) email.required = details.open;
  },
  true
);

// Card hover slideshow: horizontal pointer position picks one of the card's images.
document.addEventListener('mousemove', (event) => {
  const media = event.target.closest?.('.product-card__media--slideshow');
  if (!media) return;
  const images = [...media.querySelectorAll('.product-card__image')];
  const rect = media.getBoundingClientRect();
  const index = Math.min(images.length - 1, Math.floor(((event.clientX - rect.left) / rect.width) * images.length));
  if (media.dataset.slide === String(index)) return;
  media.dataset.slide = index;
  images.forEach((img, i) => img.classList.toggle('is-shown', i === index));
  media.querySelectorAll('.product-card__dots span').forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  // Lazy slides load on first hover.
  images[index]?.querySelector('img[loading="lazy"]')?.setAttribute('loading', 'eager');
});
document.addEventListener('mouseout', (event) => {
  const media = event.target.closest?.('.product-card__media--slideshow');
  if (!media || media.contains(event.relatedTarget)) return;
  delete media.dataset.slide;
  media.querySelectorAll('.is-shown, .is-active').forEach((el) => el.classList.remove('is-shown', 'is-active'));
});

// <rz-countdown data-end="ISO date">: days/hours/minutes/seconds to a date in the store's time zone.
if (!customElements.get('rz-countdown')) {
  customElements.define(
    'rz-countdown',
    class extends HTMLElement {
      connectedCallback() {
        this.end = Date.parse(this.dataset.end);
        if (Number.isNaN(this.end)) return;
        this.fields = ['days', 'hours', 'minutes', 'seconds'].map((k) => this.querySelector(`[data-${k}]`));
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      tick() {
        const left = Math.max(0, this.end - Date.now());
        const values = [left / 864e5, (left / 36e5) % 24, (left / 6e4) % 60, (left / 1e3) % 60].map(Math.floor);
        this.fields.forEach((el, i) => el && (el.textContent = String(values[i]).padStart(i ? 2 : 1, '0')));
        if (left > 0) return;
        clearInterval(this.timer);
        const endText = this.querySelector('.countdown__end');
        if (endText) endText.hidden = false;
        if (this.hasAttribute('data-hide-on-end')) this.querySelector('.countdown__units').hidden = true;
      }
    }
  );
}

// <rz-tabs>: WAI-ARIA tabs (click, arrow keys, Home/End).
if (!customElements.get('rz-tabs')) {
  customElements.define(
    'rz-tabs',
    class extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => {
            const map = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: this.tabs.length - 1 };
            if (!(e.key in map)) return;
            e.preventDefault();
            this.select((map[e.key] + this.tabs.length) % this.tabs.length, true);
          });
        });
      }

      select(index, focus = false) {
        this.tabs.forEach((tab, i) => {
          const on = i === index;
          tab.setAttribute('aria-selected', String(on));
          tab.tabIndex = on ? 0 : -1;
          document.getElementById(tab.getAttribute('aria-controls')).hidden = !on;
        });
        if (focus) this.tabs[index].focus();
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * Effects (Theme settings > Effects and advanced, Cart)
 * ------------------------------------------------------------------------ */
{
  const cfg = window.theme || {};
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Reveal sections and cards as they scroll into view.
  const root = document.documentElement;
  const animate =
    root.classList.contains('animations') &&
    !reduced &&
    (root.classList.contains('animations--all') || matchMedia('(min-width: 990px)').matches) &&
    !window.Shopify?.designMode;
  if (animate) {
    // Cards that come into view together appear one after another (Theme settings > Effects > Stagger).
    const stagger = cfg.revealStagger || 0;
    const observer = new IntersectionObserver(
      (entries) =>
        entries
          .filter((e) => e.isIntersecting)
          .forEach((e, i) => {
            if (stagger && e.target.matches('li')) e.target.style.transitionDelay = `${i * stagger}ms`;
            e.target.classList.add('is-revealed');
            observer.unobserve(e.target);
          }),
      { rootMargin: '0px 0px -10% 0px' }
    );
    // First look, asynchronous (no forced layout): on screen = show as is, below the fold = animate later.
    const firstLook = new IntersectionObserver((entries) =>
      entries.forEach((e) => {
        firstLook.unobserve(e.target);
        if (e.isIntersecting || e.boundingClientRect.top < innerHeight) return;
        e.target.classList.add('reveal');
        observer.observe(e.target);
      })
    );
    const seen = new WeakSet();
    // Slider cards are left out: those hidden to the side never intersect the viewport, so they would stay
    // shifted down by .reveal and make the track scroll vertically. The slider's section reveals as a whole.
    const watch = () =>
      document
        .querySelectorAll('main > .shopify-section:not(:first-child), .product-grid > li')
        .forEach((el) => {
          if (seen.has(el)) return;
          seen.add(el);
          firstLook.observe(el);
        });
    watch();
    // New cards (filters, load more, recommendations): at most once per frame.
    let frame;
    new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(watch);
    }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
  }

  // Change the tab title while the visitor is on another tab.
  if (cfg.tabMessages?.length) {
    const original = document.title;
    let timer;
    document.addEventListener('visibilitychange', () => {
      clearInterval(timer);
      if (!document.hidden) {
        document.title = original;
        return;
      }
      let i = 0;
      const messages = cfg.tabMessages.filter(Boolean);
      if (!messages.length) return;
      document.title = messages[0];
      timer = setInterval(() => (document.title = messages[++i % messages.length]), (cfg.tabMessageDelay || 3) * 1000);
    });
  }

  // Shake the cart icon every N page views while the cart has items.
  if (cfg.cartShake && !reduced) {
    try {
      const views = Number(sessionStorage.getItem('rz-views') || 0) + 1;
      sessionStorage.setItem('rz-views', views);
      const count = Number(document.querySelector('.header__cart .header__cart-count')?.textContent || 0);
      if (count > 0 && views % cfg.cartShake === 0) {
        setTimeout(() => document.querySelector('.header__cart')?.classList.add('is-shaking'), 1500);
      }
    } catch (e) {}
  }

  // Sticky header that slides away (or folds its menu / mobile search row) on scroll down and comes back on scroll up.
  const hiding = document.querySelector('[data-hide-on-scroll], [data-compact-on-scroll]')?.closest('.shopify-section');
  if (hiding) {
    const compact = !hiding.querySelector('[data-hide-on-scroll]');
    const hoverable = matchMedia('(hover: hover)');
    let lastY = scrollY;
    let frame;
    addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const y = scrollY;
          // :hover sticks on touch screens after a tap, so it only counts where there is a real pointer.
          const busy = hiding.matches(hoverable.matches ? ':focus-within, :hover' : ':focus-within') || document.querySelector('dialog[open]');
          if (Math.abs(y - lastY) < 4) return;
          const down = y > lastY && y > hiding.offsetHeight && !busy;
          lastY = y;
          if (!compact) return hiding.classList.toggle('is-header-hidden', down);
          if (down === hiding.classList.contains('is-header-compact')) return;
          if (down) {
            // Measure the folded header once with transitions off, so the margin makes up exactly for what folds away.
            const row = [...hiding.querySelectorAll('.header__bottom, .header__search-mobile')].find((el) => el.offsetHeight);
            if (!row) return;
            const rowHeight = row.offsetHeight;
            const open = hiding.offsetHeight;
            hiding.classList.add('is-header-measure', 'is-header-compact');
            const gap = open - hiding.offsetHeight;
            hiding.style.setProperty('--compact-row', `${rowHeight}px`);
            hiding.style.setProperty('--compact-gap', `${gap}px`);
            hiding.classList.remove('is-header-compact');
            void hiding.offsetHeight; // back to open while transitions are still off, so the fold below animates
            hiding.classList.remove('is-header-measure');
          }
          hiding.classList.toggle('is-header-compact', down);
        });
      },
      { passive: true }
    );
  }

  // Short vibration after adding to cart (phones that support it).
  if (cfg.vibrate) document.addEventListener('rz:cart:add', () => navigator.vibrate?.(40));

  // External links open in a new tab.
  if (cfg.externalNewTab) {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="http"]');
      if (link && link.host !== location.host && !link.target) {
        link.target = '_blank';
        link.rel = 'noopener';
      }
    });
  }
}
